<script setup>
import Buttons from '@/components/ui/Buttons.vue';
import { formatTelLink } from '@/composables/useFormatTelLink';
const props = defineProps({
  ukravtoGroup: {
    type: Object,
    default: () => {},
  },
});

const menuColumns = computed(() => {
  if (!props.ukravtoGroup?.nav?.length) return { left: [], right: [] };
  const nav = props.ukravtoGroup.nav;
  const half = Math.ceil(nav.length / 2);
  return {
    left: nav.slice(0, half),
    right: nav.slice(half),
  };
});

const openItems = ref(new Set());
const toggleSubmenu = (sectionTitle) => {
  if (openItems.value.has(sectionTitle)) {
    openItems.value.delete(sectionTitle);
  } else {
    openItems.value.add(sectionTitle);
  }
  openItems.value = new Set(openItems.value);
};

const isOpen = (sectionTitle) => {
  return openItems.value.has(sectionTitle);
};
</script>

<template>
  <div class="header__ukravto">
    <div
      v-if="ukravtoGroup.nav && ukravtoGroup.nav.length > 0"
      class="header__ukravto-nav header__ukravto-nav--grid"
    >
      <ul class="m-0 p-0 header__ukravto-nav-column">
        <li
          v-for="section in menuColumns.left"
          :key="section.title"
          class="m-0 header__ukravto-nav-item"
          :class="{ 'header__ukravto-nav-item--open': isOpen(section.title) }"
        >
          <div
            class="header__ukravto-nav-item-parent"
            :class="{
              'header__ukravto-nav-item-parent--with-children':
                section.children && section.children.length > 0,
            }"
          >
            <CTagDetect
              :to="section.url"
              class="header__ukravto-nav-item-link"
              >{{ section.title }}</CTagDetect
            >
            <CIcon
              v-if="section.children && section.children.length > 0"
              name="direction-small-white"
              width="16"
              height="16"
              class="header__ukravto-nav-item-button"
              @click="toggleSubmenu(section.title)"
            />
          </div>
          <ul
            v-if="section.children && section.children.length > 0"
            class="m-0 p-0 header__ukravto-subnav"
            :class="{ 'header__ukravto-subnav--open': isOpen(section.title) }"
          >
            <li
              v-for="item in section.children"
              :key="item.title"
              class="m-0 header__ukravto-subnav-item"
            >
              <CTagDetect
                :to="item.url"
                class="header__ukravto-subnav-item-link"
                >{{ item.title }}</CTagDetect
              >
            </li>
          </ul>
        </li>
      </ul>

      <ul class="m-0 p-0 header__ukravto-nav-column">
        <li
          v-for="section in menuColumns.right"
          :key="section.title"
          class="m-0 header__ukravto-nav-item"
          :class="{ 'header__ukravto-nav-item--open': isOpen(section.title) }"
        >
          <div
            class="header__ukravto-nav-item-parent"
            :class="{
              'header__ukravto-nav-item-parent--with-children':
                section.children && section.children.length > 0,
            }"
          >
            <CTagDetect
              :to="section.url"
              class="header__ukravto-nav-item-link"
              >{{ section.title }}</CTagDetect
            >
            <CIcon
              v-if="section.children && section.children.length > 0"
              name="direction-small-white"
              width="16"
              height="16"
              class="header__ukravto-nav-item-button"
              @click="toggleSubmenu(section.title)"
            />
          </div>
          <ul
            v-if="section.children && section.children.length > 0"
            class="m-0 p-0 header__ukravto-subnav"
            :class="{ 'header__ukravto-subnav--open': isOpen(section.title) }"
          >
            <li
              v-for="item in section.children"
              :key="item.title"
              class="m-0 header__ukravto-subnav-item"
            >
              <CTagDetect
                :to="item.url"
                class="header__ukravto-subnav-item-link"
                >{{ item.title }}</CTagDetect
              >
            </li>
          </ul>
        </li>
      </ul>
    </div>
    <div v-if="ukravtoGroup?.about" class="header__ukravto-about">
      <div class="header__ukravto-about-top">
        <p
          class="mb-0 header__ukravto-about-title h4"
          v-html="ukravtoGroup?.about?.title"
        ></p>
        <p
          class="mb-0 header__ukravto-about-description body-3-regular"
          v-html="ukravtoGroup?.about?.description"
        ></p>
      </div>
      <div class="header__ukravto-about-bottom">
        <ul class="m-0 p-0 header__ukravto-about-contacts">
          <li class="m-0 header__ukravto-about-contact">
            <div
              v-if="
                ukravtoGroup?.about?.phone &&
                ukravtoGroup?.about?.phone.length > 0
              "
              class="header__ukravto-about-contact-link"
            >
              <CIcon
                class="header__ukravto-about-contact-icon"
                name="phone-red"
                width="24"
                height="24"
              />
              <a
                :href="`tel:${formatTelLink(ukravtoGroup?.about?.phone)}`"
                class="body-3-bold"
                target="_blank"
              >
                {{ ukravtoGroup?.about?.phone }}</a
              >
            </div>
          </li>
          <li class="header__ukravto-about-contact">
            <a
              :href="`mailto:${ukravtoGroup?.about?.email}`"
              class="header__ukravto-about-contact-link body-3-bold"
            >
              <CIcon
                class="header__ukravto-about-contact-icon"
                name="email-red"
                width="24"
                height="24"
              />{{ ukravtoGroup?.about?.email }}</a
            >
          </li>
          <li class="header__ukravto-about-contact">
            <div class="header__ukravto-about-contact-link body-3-bold">
              <CIcon
                class="header__ukravto-about-contact-icon"
                name="location-red"
                width="24"
                height="24"
              />{{ ukravtoGroup?.about?.location }}
            </div>
          </li>
        </ul>
        <Buttons
          class="header__ukravto-about-button"
          :template="'header'"
          :buttons-prefix-class="'js-button-header'"
          :buttons="[
            {
              id: 1,
              type: 'popup',
              title: $t('header.get_consultation'),
              popup: 'consultation',
            },
          ]"
        />
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.header__ukravto {
  $parent: &;
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin: 0 auto;
  max-width: 1184px;
  @include media-breakpoint-up(sm) {
    gap: 32px;
  }
  @include media-breakpoint-up(md) {
    align-items: start;
    gap: 16px;
    grid-template-columns: repeat(2, 6fr);
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 16);
    @include responsive-spacing(max-width, 1184);
  }
  &-nav {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    @include media-breakpoint-up(md) {
      display: grid;
      grid-template-columns: repeat(2, 6fr);
      gap: 16px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(column-gap, 16);
    }
    &-column {
      display: flex;
      flex-direction: column;
      gap: 8px;
      list-style: none;
      @include media-breakpoint-up(md) {
        gap: 0;
      }
    }
    &-item {
      break-inside: avoid;
      page-break-inside: avoid;
      -webkit-column-break-inside: avoid;
      &-parent {
        &--with-children {
          align-items: center;
          display: grid;
          gap: 8px;
          grid-template-columns: max-content 16px;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(gap, 8);
            grid-template-columns: max-content px-to-vw(16);
          }
        }
      }
      &-link {
        @include h5;
        display: inline-flex;
        padding: 10px 0 9px;
        @include media-breakpoint-up(md) {
          @include link-normal;
          padding: 16px 0;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-top, 16);
          @include responsive-spacing(padding-bottom, 16);
        }
      }
      &-button {
        transform: rotate(0deg);
        @include media-breakpoint-up(md) {
          transform: rotate(90deg);
        }
        transition: transform 0.3s ease;
        cursor: pointer;
      }
      &--open {
        #{$parent} {
          &-subnav {
            max-height: 500px;
            opacity: 1;
            visibility: visible;
            @include media-breakpoint-up(xl) {
              @include responsive-spacing(max-height, 500);
            }
          }
          &-nav-item-button {
            transform: rotate(-90deg);
          }
        }
      }
    }
  }
  &-subnav {
    break-inside: avoid;
    page-break-inside: avoid;
    -webkit-column-break-inside: avoid;
    overflow: hidden;
    transition: all 0.3s ease;
    max-height: 0;
    opacity: 0;
    visibility: hidden;
    padding-top: 0;
    list-style: none;
    &-item {
      &-link {
        @include h6;
        color: $on-surface-variant;
        display: inline-flex;
        padding: 9px 0;
        @include media-breakpoint-up(md) {
          @include link-small;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-top, 9);
          @include responsive-spacing(padding-bottom, 9);
        }
      }
    }
  }
  &-about {
    background-color: rgba($surface-container-opacity, 0.5);
    border-radius: 8px;
    padding: 24px 16px;
    @include media-breakpoint-up(sm) {
      display: grid;
      grid-template-columns: repeat(2, 6fr);
      padding: 24px;
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: 1fr;
      margin-top: 16px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
      @include responsive-spacing(margin-top, 16);
      @include responsive-spacing(padding, 24);
    }
    &-top {
      display: grid;
      grid-template-columns: 1fr;
      gap: 16px;
      padding-bottom: 24px;
      @include media-breakpoint-up(sm) {
        gap: 48px;
        padding: 0 32px 0 0;
      }
      @include media-breakpoint-up(md) {
        grid-template-columns: calc(44% - 20px) calc(56% - 20px);
        gap: 40px;
        padding: 0 0 40px 0;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 40);
        @include responsive-spacing(padding-bottom, 40);
        grid-template-columns: calc(44% - px-to-vw(20)) calc(56% - px-to-vw(20));
      }
    }
    &-bottom {
      display: grid;
      gap: 24px;
      grid-template-columns: 1fr;
      padding-top: 24px;
      position: relative;
      &:before {
        content: '';
        background-color: $on-surface-variant-2;
        height: 1px;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
      }
      @include media-breakpoint-up(sm) {
        align-content: space-between;
        gap: 40px;
        padding: 0 0 0 32px;
        &:before {
          height: 100%;
          width: 1px;
        }
      }
      @include media-breakpoint-up(md) {
        align-items: end;
        border: unset;
        gap: 40px;
        grid-template-columns: calc(44% - 20px) calc(56% - 20px);
        padding: 40px 0 0 0;
        &:before {
          height: 1px;
          left: calc(44% + 20px);
          width: calc(56% - 20px);
        }
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 40);
        @include responsive-spacing(padding-top, 40);
        grid-template-columns: calc(44% - px-to-vw(20)) calc(56% - px-to-vw(20));
        &:before {
          @include responsive-spacing(height, 1);
          left: calc(44% + px-to-vw(20));
          width: calc(56% - px-to-vw(20));
        }
      }
    }
    &-contacts {
      display: flex;
      flex-direction: column;
      gap: 16px;
      list-style: none;
      @include media-breakpoint-up(md) {
        order: 2;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
      }
    }
    &-contact {
      &-link {
        align-items: start;
        display: grid;
        gap: 16px;
        grid-template-columns: 24px auto;
        max-width: fit-content;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 16);
          grid-template-columns: px-to-vw(24) auto;
        }
      }
      &-icon {
        height: auto;
        width: 100%;
      }
    }
    &-buttons {
      @include media-breakpoint-up(md) {
        order: 1;
      }
    }
  }
}
</style>
